import { useEffect, useRef, useState } from 'react';
import { Loader, Table, Group } from '@mantine/core';
import Header from '../../components/util/header';
import { useSelector } from 'react-redux';
import {
  fetchCashCollectionList,
  fetchCashCollectionReport,
  fetchCashCollectionXLS,
  fetchTransactionLog,
  Transaction
} from '../../../libraries/transactions';
import { AppState } from '../../client-redux/reducers';
import { LoginState } from '../../client-redux/login/reducer';
import { Language } from '../../languages/languageHandler';
import { formatCurrAmount } from '../../utils/script';
import PaginationComponent from '../../components/pagination';
import { ICurrency } from '../../../libraries/currencies';
import { ILocation } from '../../../libraries/location';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import { formatDateTimeNSecLabel, formatDateToISO } from '../../utils/date';
import ShowTransaction from './show-transactions';
import { ReactComponent as CheckIcon } from '../../../assets/img/check.svg';
import { useStatusOperDialog, isErrorResponse, isZarphSuccessResponse } from '../../context/status-oper-dialog';
import './styles.scss';

const hideColumns = {
  method: false,
  amountReceived: false,
  amountDispensed: false,
  doc: false,
  card: true,
  account: true,
  depositor: true,
  reason: true,
  declaration: true
};

const CashCollections = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const login = useSelector<AppState, LoginState>(state => state.login);
  const locations = useSelector<AppState, ILocation[]>(state => state.locations.locations);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');

  const controller = useRef(new AbortController()).current;
  const [openDetails, setOpenDetails] = useState<boolean>(true);
  const [, setActiveRow] = useState<number>();
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction>();
  const [selectedTransactionCurrency, setSelectedTransactionCurrency] = useState<ICurrency>();
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();

  const isDepositMachine = login.equipmentTypes ? login.equipmentTypes.some(e => Math.floor(e / 100) === 4) : false;
  const isPaymentMachine = login.equipmentTypes ? login.equipmentTypes.some(e => Math.floor(e / 100) !== 4) : false;

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (isDepositMachine && !isPaymentMachine) {
      hideColumns.method = true;
      hideColumns.amountReceived = true;
      hideColumns.amountDispensed = true;
      hideColumns.doc = true;
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filterBarData && limit) {
      loadCashCollectionList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadCashCollectionList = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setTransactions([]);
    setTotalRows(0);

    fetchCashCollectionList(
      controller,
      filterBarData.entity || 0,
      filterBarData.lid || 0,
      filterBarData.eid || 0,
      offset,
      limit,
      true,
      filterBarData.noteSN,
      filterBarData.transactionId,
      formatDateToISO(filterBarData.beginDateTime),
      formatDateToISO(filterBarData.endDateTime),
      filterBarData.uid,
      undefined
    )
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          response?.data?.transactions?.forEach((t: Transaction) => {
            t.cashCollectionData = t.typeData && !t.cashCollectionData ? JSON.parse(t.typeData) : undefined;
          });

          setTransactions(response?.data?.transactions || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // ----------------- Modal information with logs -------------------//
  const showTransactionDetails = (n: number) => {
    if (transactions) {
      setActiveRow(n);
      setSelectedTransaction(transactions[n]);
      if (currencies) {
        let matchingCurrency = currencies.find(currency => currency.cid === transactions[n].cid);
        setSelectedTransactionCurrency(matchingCurrency);
      }
      setOpenDetails(true);
    }
  };

  const getLog = async () => {
    if (!selectedTransaction) {
      handleApiError(new Error(lang.TRANSACTION_NO_LOG), lang.TRANSACTION_NO_LOG);
      return;
    }

    try {
      const response = await fetchTransactionLog(
        controller,
        selectedTransaction.eid,
        selectedTransaction.period,
        selectedTransaction.code
      );

      if (isErrorResponse(response) || response?.data.statusOper?.code !== 0) {
        handleApiError(response, lang.TRANSACTION_LOG_ERROR);
        return;
      }

      // success case (optional handling)
    } catch (err) {
      handleApiError(err, lang.TRANSACTION_LOG_ERROR);
    }
  };

  const getReport = async () => {
    if (!selectedTransaction) {
      handleApiError(new Error(lang.TRANSACTION_NO_LOG), lang.TRANSACTION_NO_LOG);
      return;
    }

    try {
      const response = await fetchCashCollectionReport(
        controller,
        selectedTransaction.eid,
        selectedTransaction.period,
        selectedTransaction.code
      );

      if (isErrorResponse(response) || response?.data.statusOper?.code !== 0) {
        handleApiError(response, lang.TRANSACTION_REPORT_ERROR);
        return;
      }

      // success case (optional handling)
    } catch (err) {
      handleApiError(err, lang.TRANSACTION_REPORT_ERROR);
    }
  };

  //----------------------------------------------------------------//

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const getXLSReport = async () => {
    if (!filterBarData) return;

    try {
      await fetchCashCollectionXLS(
        filterBarData.entity || 0,
        filterBarData.lid || 0,
        filterBarData.eid || 0,
        controller,
        formatDateToISO(filterBarData.beginDateTime),
        formatDateToISO(filterBarData.endDateTime),
        filterBarData.uid
      );
    } catch (err: any) {
      handleApiError(err, lang.TRANSACTIONS_XLS_ERROR);
    }
  };

  return (
    <div className="transactions">
      {openDetails && selectedTransaction && selectedTransactionCurrency ? (
        <ShowTransaction
          transaction={selectedTransaction}
          openDetails={openDetails}
          hideBtn={() => setOpenDetails(false)}
          lang={lang}
          currency={selectedTransactionCurrency}
          getLog={async () => getLog()}
          getReport={async () => getReport()}
        />
      ) : null}

      <Header pageTitle={lang.CASH_COLLECTIONS} />

      <FilterBar
        onDataChange={handleFilterBarOnDataChange}
        onSearch={loadCashCollectionList}
        fieldsUsage={{
          entity: true,
          lid: true,
          eid: true,
          uid: true,
          period: false,
          beginDateTime: true,
          endDateTime: true,
          noteSN: true,
          transactionId: true,
          financialOnly: false
        }}
        xlsReport={transactions.length ? getXLSReport : undefined}
      />

      <div ref={tableWrapperRef}>
        <Table.ScrollContainer minWidth={500} ref={tableRef}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{lang.EQUIPMENT_LOCAL}</Table.Th>
                <Table.Th>{lang.EQUIPMENT_ID}</Table.Th>
                <Table.Th>{lang.TRANSACTION_ID}</Table.Th>
                <Table.Th>{lang.GLOBAL_DATE_TIME}</Table.Th>
                <Table.Th>{lang.GLOBAL_TOTAL_INVOICED}</Table.Th>
                <Table.Th>{lang.GLOBAL_TOTAL_COLLECTED}</Table.Th>
                <Table.Th>{lang.CASH_COLLECTION_EXCESS}</Table.Th>
                <Table.Th>{lang.CASH_COLLECTION_WORKING_CAPITAL_EXCESS_REMOVED_SHORT}</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={16}>
                    <Loader color="#016273" />
                  </Table.Td>
                </Table.Tr>
              ) : (
                <>
                  {!transactions.length ? (
                    <Table.Tr>
                      <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                        {lang.GLOBAL_NO_RESULTS}
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    transactions.map((transaction, index) => {
                      const matchingCurrency = currencies?.find(c => c.cid === transaction?.cid);
                      const matchingLocation = locations.find(shop => shop.sid === transaction.sid);
                      return (
                        <Table.Tr
                          key={index}
                          style={{ cursor: 'pointer' }}
                          onClick={() => showTransactionDetails(index)}
                        >
                          <Table.Td>{matchingLocation ? matchingLocation.name : ''}</Table.Td>
                          <Table.Td>{transaction.eid}</Table.Td>
                          <Table.Td>{`${transaction.eid}-${transaction.period}-${transaction.code}`}</Table.Td>
                          <Table.Td>{formatDateTimeNSecLabel(transaction.dateTime, region)}</Table.Td>
                          <Table.Td>
                            {matchingCurrency
                              ? formatCurrAmount(transaction.cashCollectionData?.invoiced ?? 0, matchingCurrency)
                              : '-'}
                          </Table.Td>
                          <Table.Td>
                            {matchingCurrency ? formatCurrAmount(transaction.value ?? 0, matchingCurrency) : '-'}
                          </Table.Td>
                          <Table.Td>
                            {matchingCurrency
                              ? formatCurrAmount(transaction.cashCollectionData?.excess ?? 0, matchingCurrency)
                              : '-'}
                          </Table.Td>
                          <Table.Td>
                            {transaction.cashCollectionData?.excessWCRemoved ? (
                              <CheckIcon className="status-ok" />
                            ) : (
                              '-'
                            )}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })
                  )}
                </>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </div>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default CashCollections;
